/**
 * SearchOrchestrator.js - THE BRAIN
 */

import { getFirestore, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import * as searchEngine from '../utils/search.engine';
import { getApiUrl, IS_LOCALHOST, SUSTAINABILITY_DEV_MODE as DEV_MODE } from '../utils/url.utils';

// Helper to log in DEV_MODE or if 'inspect=1' is in the URL
const logDev = (msg, type = 'log') => {
  const isInspectMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('inspect') === '1';
  if (DEV_MODE || isInspectMode) {
    switch (type) {
      case 'warn':
        console.warn(`[DEV_MODE_LOG]: ${msg}`);
        break;
      case 'error':
        console.error(`[DEV_MODE_LOG]: ${msg}`);
        break;
      default:
        console.log(`[DEV_MODE_LOG]: ${msg}`);
    }
  }
};

const logPhase = (msg, color = "#ccc") => {
  const isInspectMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('inspect') === '1';
  if (DEV_MODE || isInspectMode) {
    console.log(`%c${msg}`, `color: ${color}; font-weight: bold`);
  }
};

// Facility code helper consistent with search.engine.js
const getFacilityCode = (lot) => {
  const singleLot = lot.length > 5 ? lot.slice(4, 5) : lot.slice(4);
  if (!isNaN(+singleLot.charAt(0))) return "la";
  if (singleLot.charAt(0).toUpperCase() === "M") return "mt";
  return "cn";
};

/**
 * Executes the full Flow 1 search lifecycle.
 */
export const executeSearch = async (lotNumber, { syncToFirestore = false } = {}) => {
  logDev(`Initiating high-integrity search for ${lotNumber}`);

  let data = await searchEngine.fetchLotData(lotNumber);

  // 4. Fallback Phase: If Beta Engine phases (CDN/Mirror/Firestore) failed, try V1 API directly.
  if (!data) {
    logDev(`Engine phases failed. Attempting Direct Legacy Fetch for: ${lotNumber}`);
    try {
      const response = await fetch(`https://v1.trackmydown.com/blendmixData.aspx?lotno=${lotNumber}`);
      if (!response.ok) {
        logDev(`Direct Legacy Fetch HTTP Error: ${response.status}`, 'error');
      }
      const legacyData = await response.json();
      
      if (legacyData && legacyData.MSG !== "Not Found") {
        //console.log("[DEV_MODE_LOG]: RAW V1 Legacy Data:", legacyData);
        logPhase("Resolved via Direct Legacy V1 API (Phase D)", "#2196F3");
        // Utilize existing engine normalizer to keep everything consistent
        data = searchEngine.normalizeData(legacyData, "Legacy V1 API");
        //console.log("[DEV_MODE_LOG]: NORMALIZED V1 Legacy Data:", data);
      } else {
        logDev(`Direct Legacy Fetch returned: ${legacyData ? legacyData.MSG : 'null'}`, 'warn');
      }
    } catch (err) {
      logDev(`Direct Legacy Fetch failed: ${err.message}`, 'error');
    }
  }

  if (data) {
    if (syncToFirestore) {
      performIntelligentSync(data).catch(err => logDev(`Background sync failed: ${err.message}`, 'error'));
    }
    
    if (!data.impact) {
      logDev("Valid lot found, but impact missing. Queueing...");
      triggerDiscoveryPing(data);
    }
  } else {
    logDev("Search failed to resolve via all phases (including Legacy).", 'warn');
  }

  return data;
};

/**
 * Intelligent Migration Hook (Audit-Only)
 * Idempotent "Check-Only" operation to verify migration targets.
 */
const performIntelligentSync = async (data) => {
  logDev(`Intelligent Sync triggered for: ${data.lot_number}`); // Changed log message
  
  const db = getFirestore();
  const docRef = doc(db, `afd/${getFacilityCode(data.lot_number)}/${data.lot_number}/verified`);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    try {
      await setDoc(docRef, { // ACTUAL WRITE
        ...data,
        createdAt: serverTimestamp() // Add a timestamp for when the record was created
      });
      logDev(`Wrote new V1 data for lot ${data.lot_number} to Firestore.`);
    } catch (firebaseWriteError) {
      logDev(`Failed to write new V1 data for lot ${data.lot_number} to Firestore: ${firebaseWriteError.message}`, 'error');
    }
  } else {
    logDev(`Record already exists for lot ${data.lot_number}. Skipping write.`);
  }

  // After successful write or if record exists, trigger PDF migration
  triggerPdfMigration(data.lot_number).catch(err => logDev(`PDF Migration trigger failed: ${err.message}`, 'error'));
};

/**
 * Internal Helper: Trigger PDF Migration
 */
const triggerPdfMigration = (lotNumber) => {
  logDev(`Attempting to trigger PDF migration for lot: ${lotNumber}`);
  const payload = {
    lotNumber: String(lotNumber),
    action: 'migrate_v1_pdf_to_cdn',
    timestamp: new Date().toISOString()
  };

  fetch(getApiUrl('core/fire-tr.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).catch(err => {
    logDev(`PDF Migration Signal Failed: ${err.message}`, 'error');
  });
};

/**
 * Internal Helper: The Discovery Signal
 */
const triggerDiscoveryPing = (data) => {
  const payload = {
    production_lot: String(data.production_lot || ''),
    display_lot: String(data.display_lot || data.lot_number || ''),
    prefix: String(data.prefix || ''),
    timestamp: new Date().toISOString(),
    action: 'impact_discovery_queue',
    source: String(data.source || 'Unknown') // Add source of data
  };

  fetch(getApiUrl('core/telemetry/impactQueue.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).catch(err => {
    logDev(`Telemetry Signal Failed: ${err.message}`, 'error');
  });
};
